/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        ZonesAgentPageOptimized.tsx
 * @description Page mobile optimisee pour les agents terrain - Sans scrolling
 *              Interface compacte avec grille de zones et actions rapides
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deneignementService } from '@/services/deneignementService';
import { getBackendUrl } from '@/services/api';
import { zoneService } from '@/services/zoneService';
import {
  OfflineIndicator,
  IncidentReportModal,
  InterventionRecap,
  CompactZoneGrid,
  JalonnementPanel,
  JalonPhotosSection,
} from '@/components/mobile';
import {
  ArrowLeftIcon,
  MapPinIcon,
  ClockIcon,
  PlayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CameraIcon,
  InformationCircleIcon,
  TrashIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import {
  PlayIcon as PlayIconSolid,
  StopIcon as StopIconSolid,
} from '@heroicons/react/24/solid';
import { PRIORITE_LABELS, STATUT_LABELS } from '@/types';
import { clsx } from 'clsx';

type TabType = 'zones' | 'info' | 'photos';

/**
 * Extrait les valeurs avant/apres du format genere cote backend
 * (`DeneignementController::updateImmatriculation()`) :
 * "Immatriculation avion: {ancienne} -> {nouvelle}". Fragile par nature
 * (couplage texte a une chaine formatee serveur) mais les deux cotes
 * appartiennent au meme depot ; retourne null si le format ne correspond
 * plus, auquel cas l'appelant affiche la description brute en repli.
 */
function parseImmatriculationHistorique(description: string): { ancienne: string; nouvelle: string } | null {
  const match = description.match(/^Immatriculation avion: (.+) -> (.+)$/);
  if (!match) return null;
  const normalise = (v: string) => (v === 'aucune' ? 'aucun avion' : v);
  return { ancienne: normalise(match[1]), nouvelle: normalise(match[2]) };
}

export function ZonesAgentPageOptimized() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deneignementId = Number(id);

  // Etats
  const [activeTab, setActiveTab] = useState<TabType>('zones');
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [showTerminationConfirm, setShowTerminationConfirm] = useState(false);
  const [terminationReason, setTerminationReason] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState<'avant' | 'apres' | null>(null);
  const [editingImmatriculation, setEditingImmatriculation] = useState(false);
  const [immatriculationInput, setImmatriculationInput] = useState('');

  // Queries
  const { data: intervention, isLoading: loadingIntervention } = useQuery({
    queryKey: ['deneignement', deneignementId],
    queryFn: () => deneignementService.get(deneignementId),
    enabled: !!deneignementId,
  });

  const { data: zonesData, refetch: refetchZones } = useQuery({
    queryKey: ['deneignement-zones', deneignementId],
    queryFn: () => zoneService.listDeneignementZones(deneignementId),
    enabled: !!deneignementId,
    refetchInterval: 30000,
  });

  // Mutations
  const demarrerMutation = useMutation({
    mutationFn: () => deneignementService.demarrer(deneignementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
    },
  });

  const terminerMutation = useMutation({
    mutationFn: (options?: { travauxEffectues?: boolean; motifNonRealisation?: string }) =>
      deneignementService.terminer(deneignementId, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
      setShowRecap(true);
      setShowTerminationConfirm(false);
    },
  });

  const immatriculationMutation = useMutation({
    mutationFn: (valeur: string | null) =>
      deneignementService.updateImmatriculation(deneignementId, valeur),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
      setEditingImmatriculation(false);
    },
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: ({ type, file }: { type: 'avant' | 'apres'; file: File }) =>
      deneignementService.uploadPhoto(deneignementId, type, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
      setUploadingPhoto(null);
    },
  });

  const deletePhotoMutation = useMutation({
    mutationFn: (type: 'avant' | 'apres') =>
      deneignementService.deletePhoto(deneignementId, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
    },
  });

  // Handlers
  const handlePhotoSelect = (type: 'avant' | 'apres', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadingPhoto(type);
      uploadPhotoMutation.mutate({ type, file });
    }
  };

  const handleTerminate = () => {
    const zones = zonesData?.zones || [];
    const hasAnyCompleted = zones.some((z) => z.statut === 'termine');

    if (!hasAnyCompleted) {
      setShowTerminationConfirm(true);
    } else {
      if (confirm('Terminer cette intervention ?')) {
        terminerMutation.mutate({ travauxEffectues: true });
      }
    }
  };

  // Loading
  if (loadingIntervention) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (!intervention) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100">
        <p className="text-red-500">Intervention non trouvee</p>
      </div>
    );
  }

  // Etats derives
  const canStart = ['affectee', 'en_deplacement'].includes(intervention.statut);
  const isInProgress = intervention.statut === 'en_cours';
  const isCompleted = ['cloturee', 'validee'].includes(intervention.statut);
  const zones = zonesData?.zones || [];
  const hasZoneInProgress = zones.some((z) => z.statut === 'en_cours');
  // RG-10 (SFD jalonnement Contact/Large, CA-09) : pour une intervention
  // utilisant le nouveau referentiel de jalons (`configuration` renseignee),
  // Terminer exige que TOUS les jalons soient termines, pas seulement qu'aucun
  // ne soit en cours. Garde limitee a ce cas pour ne pas changer le
  // comportement des interventions legacy (6 zones, jalonnement optionnel).
  const isNouvelleConfiguration = intervention.configuration != null;
  const jalonsRestants = zones.filter((z) => z.statut !== 'termine').length;
  const canFinish = isInProgress && (
    isNouvelleConfiguration ? zones.length > 0 && jalonsRestants === 0 : !hasZoneInProgress
  );
  const isPending = demarrerMutation.isPending || terminerMutation.isPending;

  // Historique des saisies/corrections IMAT (#118). `DeneignementController::
  // updateImmatriculation()` n'enregistre un historique qu'a partir de la
  // PREMIERE modification (la valeur saisie a la creation n'est jamais
  // tracee) : la 1ere entree 'immatriculation_maj' trouvee sert donc a la
  // fois a afficher la valeur de depart ("Saisie initiale") et la premiere
  // correction - aucune route backend dediee necessaire, `historiques` est
  // deja inclus dans la reponse de GET /deneignements/{id}.
  const imatHistorique = (intervention.historiques || [])
    .filter((h) => h.action === 'immatriculation_maj')
    .slice()
    .sort((a, b) => new Date(a.horodatage).getTime() - new Date(b.horodatage).getTime());

  const prioriteColor = {
    critique: 'bg-red-500',
    urgente: 'bg-orange-500',
    normale: 'bg-gray-500',
  }[intervention.priorite];

  // Elapsed time
  const getElapsedTime = () => {
    if (!intervention.heureDebutReelle) return null;
    const diffMs = Date.now() - new Date(intervention.heureDebutReelle).getTime();
    const mins = Math.floor(diffMs / 60000);
    return mins >= 60 ? `${Math.floor(mins / 60)}h${mins % 60}` : `${mins}min`;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Header compact */}
      <header className="bg-white shadow-sm flex-shrink-0">
        <div className="px-3 py-2 flex items-center gap-2">
          <button
            onClick={() => navigate('/agent')}
            className="p-1.5 -ml-1 rounded-full hover:bg-gray-100"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-bold text-gray-900 truncate">{intervention.numeroOrdre}</span>
              <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium text-white', prioriteColor)}>
                {PRIORITE_LABELS[intervention.priorite]}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <MapPinIcon className="w-3 h-3" />
                {intervention.parking?.code}
              </span>
              <span className="flex items-center gap-1">
                <ClockIcon className="w-3 h-3" />
                {new Date(intervention.heureSouhaitee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
              {isInProgress && getElapsedTime() && (
                <span className="text-blue-600 font-medium">{getElapsedTime()}</span>
              )}
            </div>
          </div>

          {/* Status badge */}
          <span
            className={clsx(
              'px-2 py-1 rounded-lg text-xs font-medium text-white',
              isInProgress && 'bg-red-500',
              canStart && 'bg-orange-500',
              isCompleted && 'bg-green-500'
            )}
          >
            {STATUT_LABELS[intervention.statut]}
          </span>
        </div>

        {/* Tabs (seulement si en cours ou termine) */}
        {(isInProgress || isCompleted) && (
          <div className="flex border-t border-gray-100">
            <button
              onClick={() => setActiveTab('zones')}
              className={clsx(
                'flex-1 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'zones'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500'
              )}
            >
              {isNouvelleConfiguration ? 'Jalons' : 'Zones'}
            </button>
            <button
              onClick={() => setActiveTab('info')}
              className={clsx(
                'flex-1 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'info'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500'
              )}
            >
              Infos
            </button>
            <button
              onClick={() => setActiveTab('photos')}
              className={clsx(
                'flex-1 py-2 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'photos'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500'
              )}
            >
              Photos
            </button>
          </div>
        )}
      </header>

      {/* Contenu principal */}
      <main className="flex-1 overflow-hidden p-3">
        {/* Ecran de demarrage */}
        {canStart && (
          <div className="h-full flex flex-col items-center justify-center">
            <div className="bg-white rounded-2xl p-6 shadow-sm text-center max-w-sm">
              <PlayIconSolid className="w-20 h-20 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Pret a demarrer ?</h2>
              <p className="text-gray-600 mb-6">
                {intervention.compagnie.codeIata} - Parking {intervention.parking?.code}
              </p>
              <button
                onClick={() => demarrerMutation.mutate()}
                disabled={isPending}
                className="w-full py-4 px-6 bg-green-500 hover:bg-green-600 text-white text-xl font-bold rounded-xl shadow-lg shadow-green-500/30 flex items-center justify-center gap-3"
              >
                <PlayIconSolid className="w-8 h-8" />
                DEMARRER
              </button>
            </div>
          </div>
        )}

        {/* Tab Zones / Jalons */}
        {(isInProgress || isCompleted) && activeTab === 'zones' && zonesData && (
          <div className="h-full flex flex-col">
            {isNouvelleConfiguration ? (
              <JalonnementPanel
                deneignementId={deneignementId}
                configuration={intervention.configuration!}
                zones={zonesData.zones}
                onZoneUpdate={() => refetchZones()}
                readOnly={isCompleted}
              />
            ) : (
              <CompactZoneGrid
                deneignementId={deneignementId}
                zones={zonesData.zones}
                progression={zonesData.progression}
                onZoneUpdate={() => refetchZones()}
                readOnly={isCompleted}
                zoneDemandee={intervention.zone}
              />
            )}
          </div>
        )}

        {/* Tab Infos */}
        {(isInProgress || isCompleted) && activeTab === 'info' && (
          <div className="bg-white rounded-xl p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500 text-xs">Date</span>
                <p className="font-medium">
                  {new Date(intervention.dateIntervention).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Heure souhaitee</span>
                <p className="font-medium">
                  {new Date(intervention.heureSouhaitee).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Compagnie</span>
                <p className="font-medium">{intervention.compagnie.codeIata}</p>
              </div>
              <div>
                <span className="text-gray-500 text-xs">Equipe</span>
                <p className="font-medium">
                  {intervention.equipes?.map((e) => e.nom).join(', ') || '-'}
                </p>
              </div>
              {isNouvelleConfiguration && (
                <>
                  <div>
                    <span className="text-gray-500 text-xs">Situation du poste</span>
                    <p className="font-medium">
                      {intervention.parking?.situation === 'large' ? 'Large' : 'Contact'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs">Etat</span>
                    <p className="font-medium">
                      {intervention.posteOccupe ? 'Occupe' : 'Non occupe'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500 text-xs">Jalons attendus</span>
                    <p className="font-medium">{intervention.nombreJalonsAttendus ?? '-'}</p>
                  </div>
                </>
              )}
              {intervention.heureDebutReelle && (
                <div>
                  <span className="text-gray-500 text-xs">Debut reel</span>
                  <p className="font-medium text-green-600">
                    {new Date(intervention.heureDebutReelle).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              )}
              {intervention.heureFinReelle && (
                <div>
                  <span className="text-gray-500 text-xs">Fin reelle</span>
                  <p className="font-medium text-blue-600">
                    {new Date(intervention.heureFinReelle).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              )}
            </div>

            {/* Immatriculation avion (non obligatoire) */}
            <div className="pt-3 border-t">
              {editingImmatriculation ? (
                <div>
                  <span className="text-gray-500 text-xs block mb-1">Immatriculation avion</span>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={immatriculationInput}
                      onChange={(e) => setImmatriculationInput(e.target.value.toUpperCase())}
                      maxLength={12}
                      placeholder="Ex: F-GKXA"
                      className="input flex-1 uppercase text-sm"
                      autoFocus
                    />
                    <button
                      onClick={() => immatriculationMutation.mutate(immatriculationInput.trim() || null)}
                      disabled={immatriculationMutation.isPending}
                      className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium disabled:opacity-50"
                    >
                      OK
                    </button>
                    <button
                      onClick={() => setEditingImmatriculation(false)}
                      disabled={immatriculationMutation.isPending}
                      className="px-3 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm"
                    >
                      Annuler
                    </button>
                  </div>
                  {intervention.immatriculationAvion && (
                    <button
                      onClick={() => immatriculationMutation.mutate(null)}
                      disabled={immatriculationMutation.isPending}
                      className="text-xs text-gray-500 mt-2 underline"
                    >
                      Aucun avion present
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <PaperAirplaneIcon className="w-4 h-4 text-gray-400" />
                    <div>
                      <span className="text-gray-500 text-xs block">Immatriculation avion</span>
                      <p className={clsx('font-medium text-sm', !intervention.immatriculationAvion && 'text-gray-400 italic')}>
                        {intervention.immatriculationAvion || 'Aucun avion present'}
                      </p>
                    </div>
                  </div>
                  {!isCompleted && (
                    <button
                      onClick={() => {
                        setImmatriculationInput(intervention.immatriculationAvion || '');
                        setEditingImmatriculation(true);
                      }}
                      className="p-2 rounded-full hover:bg-gray-100 active:bg-gray-200"
                      title="Modifier l'immatriculation avion"
                      aria-label="Modifier l'immatriculation avion"
                    >
                      <PencilIcon className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                </div>
              )}
              {/* Historique des saisies/corrections IMAT (#118, Agent_Infos.dc.html) */}
              {imatHistorique.length > 0 && (
                <div className="text-xs text-gray-500 mt-2 space-y-0.5">
                  {imatHistorique.map((h, index) => {
                    const heure = new Date(h.horodatage).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                    const parsed = parseImmatriculationHistorique(h.description);
                    if (!parsed) {
                      return <p key={h.id}>{h.description} - {h.user.nomComplet} a {heure}</p>;
                    }
                    return (
                      <div key={h.id}>
                        {index === 0 && (
                          <p>Saisie initiale : {parsed.ancienne}</p>
                        )}
                        <p>Corrigee par {h.user.nomComplet} a {heure} ({parsed.nouvelle})</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {intervention.commentaire && (
              <div className="pt-3 border-t">
                <span className="text-gray-500 text-xs">Instructions</span>
                <p className="text-sm">{intervention.commentaire}</p>
              </div>
            )}
          </div>
        )}

        {/* Tab Photos */}
        {(isInProgress || isCompleted) && activeTab === 'photos' && (
          <div className="flex flex-col gap-3 overflow-y-auto h-full">
          {isNouvelleConfiguration && zonesData && zonesData.zones.length > 0 && (
            <JalonPhotosSection
              deneignementId={deneignementId}
              zones={zonesData.zones}
              readOnly={isCompleted}
            />
          )}
          <div className="grid grid-cols-2 gap-3">
            {/* Photo Avant */}
            <div className="bg-white rounded-xl p-3">
              <span className="text-xs font-medium text-gray-500 mb-2 block">AVANT</span>
              {intervention.photoAvant ? (
                <div>
                  <img src={getBackendUrl(intervention.photoAvant)} alt="Avant" className="w-full h-32 object-cover rounded-lg" />
                  {isInProgress && (
                    <div className="flex gap-1 mt-1">
                      <label className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-primary-50 text-primary-700 rounded cursor-pointer hover:bg-primary-100">
                        <ArrowPathIcon className="w-3.5 h-3.5" />
                        Recharger
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhotoSelect('avant', e)} />
                      </label>
                      <button
                        onClick={() => deletePhotoMutation.mutate('avant')}
                        disabled={deletePhotoMutation.isPending}
                        className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <label className={clsx(
                  'flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer',
                  isInProgress ? 'border-gray-300 hover:border-primary-400' : 'border-gray-200 bg-gray-50'
                )}>
                  {uploadingPhoto === 'avant' ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
                  ) : (
                    <>
                      <CameraIcon className="w-10 h-10 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1">
                        {isInProgress ? 'Prendre photo' : 'Non fournie'}
                      </span>
                    </>
                  )}
                  {isInProgress && (
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handlePhotoSelect('avant', e)}
                    />
                  )}
                </label>
              )}
            </div>

            {/* Photo Apres */}
            <div className="bg-white rounded-xl p-3">
              <span className="text-xs font-medium text-gray-500 mb-2 block">APRES</span>
              {intervention.photoApres ? (
                <div>
                  <img src={getBackendUrl(intervention.photoApres)} alt="Apres" className="w-full h-32 object-cover rounded-lg" />
                  {isInProgress && (
                    <div className="flex gap-1 mt-1">
                      <label className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-primary-50 text-primary-700 rounded cursor-pointer hover:bg-primary-100">
                        <ArrowPathIcon className="w-3.5 h-3.5" />
                        Recharger
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhotoSelect('apres', e)} />
                      </label>
                      <button
                        onClick={() => deletePhotoMutation.mutate('apres')}
                        disabled={deletePhotoMutation.isPending}
                        className="flex-1 flex items-center justify-center gap-1 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <label className={clsx(
                  'flex flex-col items-center justify-center h-32 border-2 border-dashed rounded-lg cursor-pointer',
                  isInProgress ? 'border-gray-300 hover:border-primary-400' : 'border-gray-200 bg-gray-50'
                )}>
                  {uploadingPhoto === 'apres' ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
                  ) : (
                    <>
                      <CameraIcon className="w-10 h-10 text-gray-400" />
                      <span className="text-xs text-gray-500 mt-1">
                        {isInProgress ? 'Prendre photo' : 'Non fournie'}
                      </span>
                    </>
                  )}
                  {isInProgress && (
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handlePhotoSelect('apres', e)}
                    />
                  )}
                </label>
              )}
            </div>
          </div>
          </div>
        )}
      </main>

      {/* Footer avec actions */}
      {isInProgress && (
        <footer className="bg-white border-t border-gray-200 p-3 flex-shrink-0 safe-area-inset-bottom">
          <div className="flex gap-3">
            <button
              onClick={() => setShowIncidentModal(true)}
              className="flex-1 py-3 px-4 rounded-xl border border-orange-300 bg-orange-50 text-orange-700 font-medium flex items-center justify-center gap-2"
            >
              <ExclamationTriangleIcon className="w-5 h-5" />
              Incident
            </button>
            <button
              onClick={handleTerminate}
              disabled={isPending || !canFinish}
              className={clsx(
                'flex-1 py-3 px-4 rounded-xl font-bold text-white flex items-center justify-center gap-2',
                canFinish
                  ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/30'
                  : 'bg-gray-300'
              )}
            >
              <StopIconSolid className="w-5 h-5" />
              Terminer
            </button>
          </div>
          {isNouvelleConfiguration && jalonsRestants > 0 && (
            <p className="text-xs text-orange-600 text-center mt-2">
              {jalonsRestants === 1
                ? '1 jalon restant avant de terminer'
                : `${jalonsRestants} jalons restants avant de terminer`}
            </p>
          )}
          {!isNouvelleConfiguration && hasZoneInProgress && (
            <p className="text-xs text-orange-600 text-center mt-2">
              Terminez d'abord la zone en cours
            </p>
          )}
        </footer>
      )}

      {/* Footer intervention terminee */}
      {isCompleted && (
        <footer className="bg-white border-t border-gray-200 p-3 flex-shrink-0 safe-area-inset-bottom">
          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center gap-2 text-green-700">
              <CheckCircleIcon className="w-6 h-6" />
              <span className="font-semibold">Terminee</span>
            </div>
            <button
              onClick={() => setShowRecap(true)}
              className="py-2 px-4 rounded-lg bg-primary-100 text-primary-700 font-medium"
            >
              Voir recap
            </button>
          </div>
        </footer>
      )}

      {/* Modals */}
      {showIncidentModal && (
        <IncidentReportModal
          deneignementId={deneignementId}
          onClose={() => setShowIncidentModal(false)}
          onSubmit={() => {
            setShowIncidentModal(false);
            queryClient.invalidateQueries({ queryKey: ['deneignement', deneignementId] });
          }}
        />
      )}

      {showRecap && intervention && zonesData && (
        <InterventionRecap
          intervention={intervention}
          zones={zonesData.zones}
          onClose={() => setShowRecap(false)}
          onDownloadPdf={() => {
            deneignementService.downloadPdf(deneignementId).then((blob) => {
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${intervention.numeroOrdre}.pdf`;
              a.click();
            });
          }}
        />
      )}

      {/* Modal terminaison sans zones */}
      {showTerminationConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="w-8 h-8 text-orange-500" />
              <h3 className="text-lg font-bold">Aucune zone traitee</h3>
            </div>
            <p className="text-gray-600 mb-4">Veuillez indiquer un motif :</p>
            <select
              className="w-full px-3 py-2 border rounded-lg mb-4"
              value={terminationReason}
              onChange={(e) => setTerminationReason(e.target.value)}
            >
              <option value="">Selectionnez...</option>
              <option value="conditions_meteo">Conditions meteo</option>
              <option value="panne_materiel">Panne materiel</option>
              <option value="parking_occupe">Parking occupe</option>
              <option value="annulation_compagnie">Annulation compagnie</option>
              <option value="autre">Autre</option>
            </select>
            <div className="flex gap-3">
              <button
                onClick={() => setShowTerminationConfirm(false)}
                className="flex-1 py-2 px-4 border rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  if (terminationReason) {
                    terminerMutation.mutate({
                      travauxEffectues: false,
                      motifNonRealisation: terminationReason,
                    });
                  }
                }}
                disabled={!terminationReason}
                className="flex-1 py-2 px-4 bg-red-500 text-white rounded-lg font-medium disabled:opacity-50"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ZonesAgentPageOptimized;
